import React, { useState, useEffect } from 'react';
import { Drawer, Form, Input, Button, Space, App } from 'antd';
import { useMutation, useLazyQuery } from '@apollo/client';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ImageUploader from "./ImageUploader";
import { CREATE_ADDRESS_RESIDENTIAL_AREA } from '../graphql/mutations/upsertAddressResidentialArea';
import {
  QUERY_STANDARDIZED_ADDRESS,
  QueryStandardizedAddressResponse,
} from '../graphql/queries/queryStandardizedAddress';
import {
    GetAddressResidentialAreaDetailResponse,
    QUERY_ADDRESS_RESIDENTIAL_AREA_DETAIL_BY_HASH
} from "../graphql/queries/getAddressResidentialAreaDetail";
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';


interface AddressInitialValues {
  residentialAddress?: string;
  standardAddress?: string;
  stateAbbreviation?: string;
  cityName?: string;
  zipcode?: string;
  residentialName?: string;
  residentialAddressHash?: string;
  reportDataId?: string | null;
  images?: Array<{ key: string; previewUrl?: string | null }>;
}

interface AddAddressResidentialAreaDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** 校验后发现小区已存在时回调，用于关闭当前抽屉并打开编辑抽屉 */
  onSwitchToEdit?: (areaId: string) => void;
  /** 预填数据，来自审核通过流程 */
  initialValues?: AddressInitialValues;
}

interface ImageSlot {
  key: string | null;       // S3 objectKey
  previewUrl: string | null; // Blob URL 用于预览
}

const DRAWER_PREFIX = 'mapTool.addressResidentialArea.drawer';
const ADD_PREFIX = 'mapTool.addressResidentialArea.addDrawer';

const AddAddressResidentialAreaDrawer: React.FC<AddAddressResidentialAreaDrawerProps> = ({
  visible,
  onClose,
  onSuccess,
  onSwitchToEdit,
  initialValues,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [createArea, { loading }] = useMutation(CREATE_ADDRESS_RESIDENTIAL_AREA);
  const [queryStandardizedAddress, { loading: validating }] = useLazyQuery<QueryStandardizedAddressResponse>(
    QUERY_STANDARDIZED_ADDRESS
  );
  const [queryDetailByHash] = useLazyQuery<GetAddressResidentialAreaDetailResponse>(
    QUERY_ADDRESS_RESIDENTIAL_AREA_DETAIL_BY_HASH
  );

  const MAX_IMAGES = 5;
  const [imageSlots, setImageSlots] = useState<ImageSlot[]>(
    Array(MAX_IMAGES).fill(null).map(() => ({ key: null, previewUrl: null }))
  );

  useEffect(() => {
    if (visible && initialValues) {
      form.setFieldsValue({
        residentialAddress: initialValues.residentialAddress ?? '',
        standardAddress: initialValues.standardAddress ?? '',
        stateAbbreviation: initialValues.stateAbbreviation ?? '',
        cityName: initialValues.cityName ?? '',
        zipcode: initialValues.zipcode ?? '',
        residentialName: initialValues.residentialName ?? '',
        residentialAddressHash: initialValues.residentialAddressHash ?? '',
      reportDataId: initialValues.reportDataId ?? null,
      });
      if (initialValues.images && initialValues.images.length > 0) {
        const next = Array(MAX_IMAGES).fill(null).map(() => ({ key: null as string | null, previewUrl: null as string | null }));
        initialValues.images.slice(0, MAX_IMAGES).forEach((img, i) => {
          next[i] = { key: img.key, previewUrl: img.previewUrl ?? null };
        });
        setImageSlots(next);
      }
    }
  }, [visible, initialValues, form]);

  const handleSlotChange = (slotIndex: number, urls: string[], previewUrls?: string[]) => {
    const objectKey = urls[0] || null;
    const previewUrl = previewUrls?.[0] || null;
    setImageSlots(prev => {
      const next = [...prev];
      next[slotIndex] = {
        key: objectKey,
        previewUrl: objectKey ? (previewUrl || prev[slotIndex].previewUrl) : null,
      };
      return next;
    });
  };

  const handleMove = (slotIndex: number, direction: 'left' | 'right') => {
    const targetIndex = direction === 'left' ? slotIndex - 1 : slotIndex + 1;
    if (targetIndex < 0 || targetIndex >= MAX_IMAGES) return;
    setImageSlots(prev => {
      const next = [...prev];
      const tmp = next[slotIndex];
      next[slotIndex] = next[targetIndex];
      next[targetIndex] = tmp;
      return next;
    });
  };

  const handleValidateAddress = async () => {
    const addr = form.getFieldValue('residentialAddress');
    if (!addr) {
      message.warning(t(`${ADD_PREFIX}.messagePleaseEnterAddress`));
      return;
    }
    try {
      const { data, error } = await queryStandardizedAddress({
        variables: { residentialAddress: addr },
      });
      if (error) {
        const errorMessage =
          error.graphQLErrors?.[0]?.message ??
          error.graphQLErrors?.[0]?.extensions?.message ??
          error.message;
        message.error(errorMessage || t(`${ADD_PREFIX}.messageValidateFailed`));
        return;
      }
      const result = data?.queryStandardizedAddress;
      if (!result) {
        message.warning(t(`${ADD_PREFIX}.messageNoStandardizedAddress`));
        return;
      }
      const hash = result.residentialAddressHash || '';
      form.setFieldsValue({
        standardAddress: result.standardAddress,
        stateAbbreviation: result.stateAbbreviation || '',
        cityName: result.cityName || '',
        zipcode: result.zipcode || '',
        residentialAddressHash: hash,
      });

      if (!hash) {
        message.success(t(`${ADD_PREFIX}.messageValidateSuccess`));
        return;
      }

      const { data: detailData } = await queryDetailByHash({
        variables: { residentialAddressHash: hash },
      });
      const existingArea = detailData?.queryAddressResidentialAreaDetail;
      if (existingArea?.id && onSwitchToEdit) {
        onSwitchToEdit(existingArea.id);
        message.info(t(`${ADD_PREFIX}.messageAreaExistsOpenEdit`));
        return;
      }

      message.success(t(`${ADD_PREFIX}.messageValidateSuccess`));
    } catch (e: any) {
      const errorMessage =
        e?.graphQLErrors?.[0]?.message ??
        e?.graphQLErrors?.[0]?.extensions?.message ??
        e?.networkError?.result?.errors?.[0]?.message ??
        e?.message ??
        t(`${ADD_PREFIX}.messageValidateFailed`);
      message.error(errorMessage);
    }
  };

  const handleSubmit = async () => {
    try {
      const hash = form.getFieldValue('residentialAddressHash');
      if (!hash || String(hash).trim() === '') {
        message.warning(t(`${ADD_PREFIX}.messagePleaseValidateFirst`));
        return;
      }
      const values = await form.validateFields();
      // 构建图片列表参数
      const images = imageSlots
        .map((slot, index) => slot.key ? { residentialAreaId: 0, imageS3Key: slot.key, stopNumber: index + 1 } : null)
        .filter((img): img is { residentialAreaId: number; imageS3Key: string; stopNumber: number } => img !== null);

      if (images.length === 0) {
        message.warning(t(`${ADD_PREFIX}.messageMinOneImage`));
        return;
      }

      await createArea({
        variables: {
          input: {
            residentialAddress: values.residentialAddress,
            standardAddress: values.standardAddress,
            residentialAddressHash: values.residentialAddressHash,
            residentialName: values.residentialName,
            cityName: values.cityName || null,
            stateAbbreviation: values.stateAbbreviation || null,
            zipcode: values.zipcode || null,
            reportDataId: values.reportDataId ? Number(values.reportDataId) : null,
            images: images.length > 0 ? images : null,
          },
        },
      });

      message.success(t(`${ADD_PREFIX}.messageAddSuccess`));
      form.resetFields();
      setImageSlots(Array(MAX_IMAGES).fill(null).map(() => ({ key: null, previewUrl: null })));
      onSuccess();
      onClose();
    } catch (e: any) {
      if (e?.name === 'ValidationError') {
        return;
      }
      message.error(e?.message || t(`${ADD_PREFIX}.messageAddFailed`));
    }
  };

  const handleClose = () => {
    form.resetFields();
    setImageSlots(Array(MAX_IMAGES).fill(null).map(() => ({ key: null, previewUrl: null })));
    onClose();
  };

  return (
    <Drawer
      title={t(`${ADD_PREFIX}.title`)}
      open={visible}
      onClose={handleClose}
      width={880}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose}>{t(`${ADD_PREFIX}.buttonCancel`)}</Button>
          <HasResource resourceCodes={[ResourceCode.addressResidentialArea.CREATE]}>
            <Button type="primary" loading={loading} onClick={handleSubmit}>
              {t(`${ADD_PREFIX}.buttonSave`)}
            </Button>
          </HasResource>
        </div>
      }
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 20 }}
      >
        <Form.Item name="residentialAddressHash" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="reportDataId" hidden>
          <Input type="hidden" />
        </Form.Item>

        {/* 第一行：小区地址输入框 + 校验按钮 */}
        <Form.Item
          label={t(`${DRAWER_PREFIX}.labelResidentialAddress`) as string}
          extra={
            <div className="text-gray-500 text-xs mt-1">
              {t(`${ADD_PREFIX}.addressFormatHint`)} {t(`${ADD_PREFIX}.addressFormatExample`)}
            </div>
          }
        >
          <div className="flex gap-0">
            <Form.Item
              name="residentialAddress"
              noStyle
              rules={[
                { required: true, message: t(`${ADD_PREFIX}.ruleResidentialAddressRequired`) as string },
                { max: 200, message: t('common.max200Characters') as string },
              ]}
            >
              <Input placeholder={t(`${ADD_PREFIX}.placeholderResidentialAddress`) as string} maxLength={200} showCount />
            </Form.Item>
            <Button
              type="default"
              loading={validating}
              onClick={handleValidateAddress}
            >
              {t(`${ADD_PREFIX}.buttonValidate`)}
            </Button>
          </div>
        </Form.Item>

        {/* 第二行：标准化地址 */}
        <Form.Item
          name="standardAddress"
          label={t(`${DRAWER_PREFIX}.labelStandardAddress`) as string}
        >
          <Input  disabled />
        </Form.Item>

        {/* 第三行：州 */}
        <Form.Item name="stateAbbreviation" label={t(`${DRAWER_PREFIX}.labelState`) as string}>
          <Input disabled />
        </Form.Item>

        {/* 第四行：城市 */}
        <Form.Item name="cityName" label={t(`${DRAWER_PREFIX}.labelCity`) as string}>
          <Input disabled />
        </Form.Item>

        {/* 第五行：邮编 */}
        <Form.Item name="zipcode" label={t(`${DRAWER_PREFIX}.labelZipcode`) as string}>
          <Input disabled />
        </Form.Item>

        {/* 小区名称放在邮编行下方，隐藏 Hash 输入组件（自动生成） */}
        <Form.Item
          name="residentialName"
          label={t(`${DRAWER_PREFIX}.labelResidentialName`) as string}
          rules={[{ max: 200, message: t('common.max200Characters') as string }]}
        >
          <Input placeholder={t(`${ADD_PREFIX}.placeholderResidentialName`) as string} maxLength={200} showCount />
        </Form.Item>

        {/* 第六行：小区图片组件 */}
        <div className="mt-4">
          <div className="mb-2 font-semibold">{t(`${DRAWER_PREFIX}.sectionImagesTitle`)}</div>
          <div className="grid grid-cols-5 gap-4">
            {imageSlots.map((slot, index) => (
              <div key={index} className="flex flex-col items-center">
                <ImageUploader
                  value={slot.key ? [slot.key] : []}
                  displayUrls={slot.previewUrl ? [slot.previewUrl] : []}
                  onChange={(urls, previewUrls) => handleSlotChange(index, urls, previewUrls)}
                  maxCount={1}
                  maxSizeMB={25}
                  tag="address-residential-area"
                  usePublicBucket
                />
                {slot.key && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="small"
                      icon={<LeftOutlined />}
                      disabled={index === 0 || !imageSlots[index - 1]?.key}
                      onClick={() => handleMove(index, 'left')}
                    />
                    <Button
                      size="small"
                      icon={<RightOutlined />}
                      disabled={index === MAX_IMAGES - 1 || !imageSlots[index + 1]?.key}
                      onClick={() => handleMove(index, 'right')}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Form>
    </Drawer>
  );
};

export default AddAddressResidentialAreaDrawer;

